import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import cn from 'classnames';
import localization from '../../shared/strings';
import Tooltip from '../Tooltip';
import { pixelsConfig } from '../DownloadDialog/config';
import {
  clampCropDimensionsForAspect,
  getCropAspectRatio,
  heightFromCropWidth,
  widthFromCropHeight,
} from '../../helpers/assets/computeLockedCropDimensions';

const GENERIC_RATIO_ROW_KEYS = [
  { key: 'original', labelKey: 'cropAspectOriginal' },
  { key: '1:1', labelKey: 'cropAspect1_1' },
  { key: '4:3', labelKey: 'cropAspect4_3' },
  { key: '3:4', labelKey: 'cropAspect3_4' },
  { key: '16:9', labelKey: 'cropAspect16_9' },
  { key: '9:16', labelKey: 'cropAspect9_16' },
];

/**
 * Keep typed value within `maxAllowed`. Native `input[type=number]` does not
 * enforce `max` while typing.
 */
function clampDimensionDraft(raw, maxAllowed) {
  if (raw === '') return raw;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return raw;
  if (n > maxAllowed) return String(maxAllowed);
  return raw;
}

function handleDimensionArrowKey(e, {
  draft, setDraft, min, max,
}) {
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return false;
  e.preventDefault();
  const parsed = parseInt(draft, 10);
  const base = Number.isFinite(parsed) ? parsed : min;
  const next = Math.min(max, Math.max(min, base + (e.key === 'ArrowUp' ? 1 : -1)));
  setDraft(String(next));
  return true;
}

/**
 * Build chip descriptors from either a curated `customRatios` list (default
 * social presets like "Instagram Post") or the generic ratio row.
 */
function toChips(customRatios, L) {
  if (!Array.isArray(customRatios) || customRatios.length === 0) {
    return GENERIC_RATIO_ROW_KEYS.map(({ key, labelKey }) => ({
      key,
      label: L[labelKey],
    }));
  }

  return customRatios.map((r) => ({
    key: r.ratio,
    label: r.ratio,
    tooltip: r.tooltip,
    width: r.width,
    height: r.height,
    size: (r.width && r.height) ? `${r.width}\u00d7${r.height}` : null,
    safeArea: r.safeArea,
  }));
}

export default function AssetsCopyCropAspectPanel({
  variant,
  aspectPreset,
  onAspectPresetChange,
  cropPixelWidth,
  cropPixelHeight,
  onCropPixelsCommit,
  sourceNaturalWidth,
  sourceNaturalHeight,
  dimensionsDisabled,
  disabled,
  lockedTooltip,
  customRatios,
}) {
  const L = localization.COPY_ASSETS_DIALOG;
  const hasCustomRatios = Array.isArray(customRatios) && customRatios.length > 0;
  const showFreeForm = variant === 'full' && !hasCustomRatios;
  const showDimensions = variant === 'full';

  const chips = toChips(customRatios, L);
  const activeChip = chips.find((c) => c.key === aspectPreset);

  // For curated social presets: if the source image can fit the preset's target
  // resolution (e.g. 1080×1350 for Instagram Post 4:5), display that target.
  // Otherwise display the real max-centered crop rect fed from the preview,
  // since the zipper's resize uses "shrink-only" and won't upscale beyond
  // source pixels. Fields are read-only in this mode — the preview crop is
  // locked to this aspect (no resize handles).
  const presetTargetW = hasCustomRatios ? activeChip?.width : undefined;
  const presetTargetH = hasCustomRatios ? activeChip?.height : undefined;
  const canFitPresetTarget = (
    presetTargetW != null
    && presetTargetH != null
    && typeof cropPixelWidth === 'number'
    && typeof cropPixelHeight === 'number'
    && cropPixelWidth >= presetTargetW
    && cropPixelHeight >= presetTargetH
  );
  const displayW = canFitPresetTarget ? presetTargetW : cropPixelWidth;
  const displayH = canFitPresetTarget ? presetTargetH : cropPixelHeight;
  const dimensionsFieldDisabled = hasCustomRatios || dimensionsDisabled;
  const dimensionsEditable = !hasCustomRatios && !dimensionsDisabled;
  const cropAspectRatio = getCropAspectRatio(aspectPreset);
  const dimensionsLinked = dimensionsEditable && cropAspectRatio != null;
  /** Max for width/height fields: never above source image (4:3, 1:1, free, etc.). */
  const pixelWidthInputMax = (
    typeof sourceNaturalWidth === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalWidth)
      : pixelsConfig.max
  );
  const pixelHeightInputMax = (
    typeof sourceNaturalHeight === 'number'
      ? Math.min(pixelsConfig.max, sourceNaturalHeight)
      : pixelsConfig.max
  );

  const [wDraft, setWDraft] = useState('');
  const [hDraft, setHDraft] = useState('');

  useEffect(() => {
    setWDraft(displayW != null ? String(displayW) : '');
    setHDraft(displayH != null ? String(displayH) : '');
  }, [displayW, displayH]);

  const commitDimensions = useCallback((driver = 'width') => {
    if (!showDimensions || !dimensionsEditable) return;
    let w = parseInt(wDraft, 10);
    let h = parseInt(hDraft, 10);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
      setWDraft(displayW != null ? String(displayW) : '');
      setHDraft(displayH != null ? String(displayH) : '');
      return;
    }
    if (dimensionsLinked && cropAspectRatio) {
      const clamped = clampCropDimensionsForAspect(
        w,
        h,
        cropAspectRatio,
        sourceNaturalWidth,
        sourceNaturalHeight,
        driver,
      );
      if (!clamped) {
        setWDraft(displayW != null ? String(displayW) : '');
        setHDraft(displayH != null ? String(displayH) : '');
        return;
      }
      w = clamped.width;
      h = clamped.height;
      setWDraft(String(w));
      setHDraft(String(h));
    } else if (typeof sourceNaturalWidth === 'number' && typeof sourceNaturalHeight === 'number') {
      w = Math.min(w, sourceNaturalWidth);
      h = Math.min(h, sourceNaturalHeight);
    }
    onCropPixelsCommit(w, h);
  }, [
    cropAspectRatio,
    dimensionsEditable,
    dimensionsLinked,
    displayH,
    displayW,
    hDraft,
    onCropPixelsCommit,
    showDimensions,
    sourceNaturalHeight,
    sourceNaturalWidth,
    wDraft,
  ]);

  const makeDimensionKeyDown = useCallback((dimension) => (e) => {
    e.stopPropagation();
    const isHeight = dimension === 'height';
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const parsed = parseInt(isHeight ? hDraft : wDraft, 10);
      const base = Number.isFinite(parsed) ? parsed : pixelsConfig.min;
      const delta = e.key === 'ArrowUp' ? 1 : -1;
      if (dimensionsLinked && cropAspectRatio) {
        const nextPrimary = base + delta;
        const clamped = clampCropDimensionsForAspect(
          isHeight ? undefined : nextPrimary,
          isHeight ? nextPrimary : undefined,
          cropAspectRatio,
          sourceNaturalWidth,
          sourceNaturalHeight,
          isHeight ? 'height' : 'width',
        );
        if (clamped) {
          setWDraft(String(clamped.width));
          setHDraft(String(clamped.height));
        }
        return;
      }
      if (handleDimensionArrowKey(e, {
        draft: isHeight ? hDraft : wDraft,
        setDraft: isHeight ? setHDraft : setWDraft,
        min: pixelsConfig.min,
        max: isHeight ? pixelHeightInputMax : pixelWidthInputMax,
      })) {
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDimensions(isHeight ? 'height' : 'width');
      e.target.blur();
    }
  }, [
    commitDimensions,
    cropAspectRatio,
    dimensionsLinked,
    hDraft,
    pixelHeightInputMax,
    pixelWidthInputMax,
    sourceNaturalHeight,
    sourceNaturalWidth,
    wDraft,
  ]);

  const handleWDraftChange = useCallback(
    (e) => {
      const raw = clampDimensionDraft(e.target.value, pixelWidthInputMax);
      setWDraft(raw);
      if (!dimensionsLinked || !cropAspectRatio || raw === '') return;
      const w = parseInt(raw, 10);
      if (!Number.isFinite(w) || w < 1) return;
      const nextH = heightFromCropWidth(w, cropAspectRatio);
      if (nextH != null) setHDraft(String(nextH));
    },
    [cropAspectRatio, dimensionsLinked, pixelWidthInputMax],
  );

  const handleHDraftChange = useCallback(
    (e) => {
      const raw = clampDimensionDraft(e.target.value, pixelHeightInputMax);
      setHDraft(raw);
      if (!dimensionsLinked || !cropAspectRatio || raw === '') return;
      const h = parseInt(raw, 10);
      if (!Number.isFinite(h) || h < 1) return;
      const nextW = widthFromCropHeight(h, cropAspectRatio);
      if (nextW != null) setWDraft(String(nextW));
    },
    [cropAspectRatio, dimensionsLinked, pixelHeightInputMax],
  );

  const renderChip = (chip) => {
    const btn = (
      <button
        key={chip.key}
        type="button"
        className={`assetsCopyDialog__formatButton${aspectPreset === chip.key ? ' is-active' : ''}`}
        onClick={() => onAspectPresetChange(chip.key)}
        disabled={disabled}
      >
        {chip.label}
      </button>
    );
    return (
      <Choose>
        <When condition={chip.tooltip}>
          <Tooltip key={chip.key} content={chip.tooltip} placement="top">{btn}</Tooltip>
        </When>
        <Otherwise>{btn}</Otherwise>
      </Choose>
    );
  };

  const ratioButtons = chips.map(renderChip);

  const wrapLocked = (content) => {
    if (!disabled || !lockedTooltip) {
      return content;
    }
    return (
      <Tooltip content={lockedTooltip} placement="top">
        <span className={cn('assetsCopyDialog__lockedSection')}>
          {content}
        </span>
      </Tooltip>
    );
  };

  if (variant === 'bulk') {
    return wrapLocked(
      <div className={cn('assetsCopyDialog__format assetsCopyDialog__cropRatioBulk', disabled && 'assetsCopyDialog__format--disabled')}>
        <div className="assetsCopyDialog__formatLabel">{L.cropRatioLabel}</div>
        <div className="assetsCopyDialog__formatButtons">
          {ratioButtons}
        </div>
      </div>,
    );
  }

  return wrapLocked(
    <div className={cn('assetsCopyDialog__cropPanel', disabled && 'assetsCopyDialog__cropPanel--disabled')}>
      <div className="assetsCopyDialog__formatLabel">{L.cropRatioLabel}</div>
      <div className="assetsCopyDialog__formatButtons assetsCopyDialog__formatButtons--wrap">
        {ratioButtons}
        <If condition={showFreeForm}>
          <button
            type="button"
            className={`assetsCopyDialog__formatButton${aspectPreset === 'free' ? ' is-active' : ''}`}
            onClick={() => onAspectPresetChange('free')}
            disabled={disabled}
          >
            {L.cropAspectFreeForm}
          </button>
        </If>
      </div>

      <If condition={showDimensions}>
        <div
          className={cn(
            'assetsCopyDialog__cropDimsRow',
            dimensionsLinked && 'assetsCopyDialog__resizeDimsRow',
          )}
        >
          <TextField
            className="assetsCopyDialog__cropDimField"
            label={L.cropWidthLabel}
            type="number"
            value={wDraft}
            onChange={handleWDraftChange}
            onBlur={() => commitDimensions('width')}
            onKeyDown={makeDimensionKeyDown('width')}
            disabled={dimensionsFieldDisabled}
            min={pixelsConfig.min}
            max={pixelWidthInputMax}
          />
          <TextField
            className="assetsCopyDialog__cropDimField"
            label={L.cropHeightLabel}
            type="number"
            value={hDraft}
            onChange={handleHDraftChange}
            onBlur={() => commitDimensions('height')}
            onKeyDown={makeDimensionKeyDown('height')}
            disabled={dimensionsFieldDisabled}
            min={pixelsConfig.min}
            max={pixelHeightInputMax}
          />
        </div>
      </If>
    </div>,
  );
}

AssetsCopyCropAspectPanel.propTypes = {
  variant: PropTypes.oneOf(['full', 'bulk']),
  aspectPreset: PropTypes.string.isRequired,
  onAspectPresetChange: PropTypes.func.isRequired,
  cropPixelWidth: PropTypes.number,
  cropPixelHeight: PropTypes.number,
  onCropPixelsCommit: PropTypes.func,
  /** Source image size in pixels (caps crop width/height inputs for generic ratios and freeform). */
  sourceNaturalWidth: PropTypes.number,
  sourceNaturalHeight: PropTypes.number,
  dimensionsDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  lockedTooltip: PropTypes.node,
  customRatios: PropTypes.arrayOf(PropTypes.shape({
    ratio: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    tooltip: PropTypes.string,
    isDefault: PropTypes.bool,
    safeArea: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  })),
};

AssetsCopyCropAspectPanel.defaultProps = {
  variant: 'full',
  cropPixelWidth: undefined,
  cropPixelHeight: undefined,
  onCropPixelsCommit: () => {},
  sourceNaturalWidth: undefined,
  sourceNaturalHeight: undefined,
  dimensionsDisabled: false,
  disabled: false,
  lockedTooltip: null,
  customRatios: null,
};
